import { PageTitleWithField } from 'components/authorable/Heroes/PageTitle/PageTitle';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import Container from 'components/helpers/Container';
import { LayoutTwoColumnShell } from 'components/authorable/layout/LayoutTwoColumn/LayoutTwoColumn';
import { Field, Text } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import HeadingTag from 'components/helpers/HeadingTag';
import { ValidHeadingLevels } from 'lib/sitecore/component-params';
import {
  TcDirectoryListingProps,
  TcDirectoryCenterProps,
  ListingFields,
  TcDirectoryTxTotalsProps,
} from './_types';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';

const CenterInfo = ({ center }: { center?: TcDirectoryCenterProps }) => {
  if (!center || !center.fields) {
    return <></>;
  }
  const { website, name, phone, tollFree, addr1, addr2, addr3, addr4, addr5, city, state, zip } =
    center.fields;
  const stateFieldString = (state?.fields?.Value as Field<string>) ?? null;
  const linkField = website.value
    ? {
        value: {
          ...website.value,
          text: name.value,
        },
      }
    : website;
  return (
    <>
      <div className={classnames('mb-4')}>
        <div>
          <LinkA11yWrapper className={TcDirectoryListingClasses.Link} field={linkField} />
        </div>
        <Text field={addr1} tag="div" />
        <Text field={addr2} tag="div" />
        <Text field={addr3} tag="div" />
        <Text field={addr4} tag="div" />
        <Text field={addr5} tag="div" />
        {(city || stateFieldString || zip) && (
          <div>
            <Text field={city} tag="span" />
            ,&nbsp;
            <Text field={stateFieldString} tag="span" />
            &nbsp;
            <Text field={zip} tag="span" />
          </div>
        )}
        {phone.value && (
          <div>
            Phone:{' '}
            <LinkA11yWrapper
              className={TcDirectoryListingClasses.Link}
              field={{
                value: {
                  href: `tel:${phone.value}`,
                  text: phone.value,
                },
              }}
            />
          </div>
        )}
        {tollFree.value && (
          <div>
            Toll-free:{' '}
            <LinkA11yWrapper
              className={TcDirectoryListingClasses.Link}
              field={{
                value: {
                  href: `tel:${tollFree.value}`,
                  text: tollFree.value,
                },
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

const LeftSection = ({
  primary,
  secondary,
}: {
  primary?: TcDirectoryCenterProps;
  secondary?: TcDirectoryCenterProps;
}): React.JSX.Element => {
  return (
    <>
      <HeadingTag
        headingLevel={'h2' as ValidHeadingLevels}
        className={classnames('heading-lg', 'text-left', 'max-w-prose', 'mb-4')}
      >
        Locations
      </HeadingTag>
      <CenterInfo center={primary} />
      <CenterInfo center={secondary} />
    </>
  );
};

const Totals = ({ totals }: { totals?: TcDirectoryTxTotalsProps }) => {
  if (!totals || !totals.fields) {
    return <></>;
  }
  const { marrowTotal, pbscTotal, cordBloodTotal, autologousTotal } = totals.fields;
  const totalProgramClasses = classnames('inline');

  return (
    <>
      <div className={classnames('mb-2', 'md:mb-4')}>
        <ul className={classnames('list-inside', 'list-disc', 'inline')}>
          <li>
            <div className={totalProgramClasses}>
              <b>Marrow:</b> {marrowTotal.value}
            </div>
          </li>
          <li>
            <div className={totalProgramClasses}>
              <b>PBSC:</b> {pbscTotal.value}
            </div>
          </li>
          <li>
            <div className={totalProgramClasses}>
              <b>Cord Blood:</b> {cordBloodTotal.value}
            </div>
          </li>
          <li>
            <div className={totalProgramClasses}>
              <b>Autologous (PBSC or Marrow):</b> {autologousTotal.value}
            </div>
          </li>
        </ul>
      </div>
    </>
  );
};

const ListingDetails = ({
  listing,
  totals,
}: {
  listing?: ListingFields;
  totals?: TcDirectoryTxTotalsProps;
}) => {
  if (!listing) {
    return <></>;
  }
  const { displayYears, programType } = listing;
  const years = displayYears.map((e) => {
    return e.displayName;
  });
  const programTypeDisplay =
    programType?.displayName === 'Both'
      ? 'adult and pediatric'
      : programType?.displayName?.toLowerCase();
  return (
    <>
      <div className={TcDirectoryListingClasses.Disclaimer}>
        From January 1, {years[0]} to December 31st, {years[1]}
      </div>
      <Totals totals={totals} />
      <div className={classnames('mb-2', 'md:mb-4')}>Performs {programTypeDisplay} transplants</div>
    </>
  );
};

const RightSection = ({
  listing,
  totals,
}: {
  listing: ListingFields;
  totals?: TcDirectoryTxTotalsProps;
}): React.JSX.Element => {
  return (
    <>
      <HeadingTag
        headingLevel={'h2' as ValidHeadingLevels}
        className={TcDirectoryListingClasses.Heading.Large}
      >
        Total transplants by cell source
      </HeadingTag>
      <ListingDetails listing={listing} totals={totals} />
    </>
  );
};

const findCenter = ({
  centers,
  primary,
}: {
  centers?: TcDirectoryCenterProps[];
  primary: boolean;
}) => {
  return centers?.find((v) => {
    return v.fields?.isPrimary?.value === primary;
  }) as TcDirectoryCenterProps;
};

const PageHeader = ({ params, fields }: TcDirectoryListingProps): React.JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const { centers, listing, totals } = fields;
  const { tcDirSearch } = listing;
  const primaryCenter = findCenter({ centers, primary: true });
  const secondaryCenter = findCenter({ centers, primary: false });

  const pageTitleFields = {
    params: {
      ...params,
      colorType: 'night',
    },
    fields: {
      pageTitle: {
        value: primaryCenter?.fields?.name?.value,
      },
    },
  };

  return (
    <Container size="standard" dataComponent="authorable/nmdpsite/tcdirectory/header">
      <PageTitleWithField params={pageTitleFields.params} fields={pageTitleFields.fields} />
      <div className={classnames('py-6', 'px-4', 'md:px-20', 'md:py-12')}>
        <LinkA11yWrapper
          className={classnames('text-blue', 'hover:text-theme-btn-link-text-hover')}
          field={{
            value: {
              href: tcDirSearch?.value.href,
              text: tcDirSearch?.value.text,
            },
          }}
        />
      </div>
      <div className={classnames('p-6', 'md:px-24', 'md:py-12')}>
        <LayoutTwoColumnShell
          left={<LeftSection primary={primaryCenter} secondary={secondaryCenter} />}
          right={<RightSection listing={listing} totals={totals} />}
        />
      </div>
    </Container>
  );
};

export default PageHeader;
